import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Google Analytics 4 integration component
 *
 * Loads GA4 script and initializes tracking with consent mode v2.
 * Script only loads if VITE_GA_MEASUREMENT_ID is set in environment.
 *
 * Automatically tracks page views on route changes (React Router client-side navigation).
 *
 * Consent handling:
 * - Initially set to 'denied' for GDPR compliance
 * - Updated to 'granted' when user accepts cookies via CookieConsent component
 *
 * VERIFICATION:
 * 1. Environment Setup:
 *    - Set VITE_GA_MEASUREMENT_ID in .env (e.g., "G-XXXXXXXXXX")
 *    - Set VITE_GA_DEBUG_MODE=true for detailed console logging
 *
 * 2. Check Script Loading:
 *    - Open browser DevTools → Elements tab
 *    - Verify <script src="https://www.googletagmanager.com/gtag/js?id=G-XXXXXXXXXX"> in <head>
 *    - Console should show: "[GA] Initialized with measurement ID: G-XXXXXXXXXX"
 *
 * 3. Test Consent Flow:
 *    - Clear localStorage: localStorage.removeItem('cookie-consent')
 *    - Reload page and navigate between routes
 *    - Console should show: "[GA] Page view not tracked - user has not consented"
 *    - Accept cookies via CookieConsent component
 *    - Navigate to different route
 *    - Console should show: "[GA] Page view tracked: /pattern/0"
 *
 * 4. Verify Network Requests:
 *    - Open DevTools → Network tab
 *    - Filter by "google-analytics.com"
 *    - Navigate between routes (e.g., / → /pattern/0 → /control-changes/0)
 *    - Look for POST requests to "google-analytics.com/g/collect" (Status: 204)
 *    - Each route change should trigger a new collect request (only if consent granted)
 *
 * 5. GA4 DebugView (Real-time Validation):
 *    - Go to GA4 property → Configure → DebugView
 *    - With VITE_GA_DEBUG_MODE=true, events appear in real-time
 *    - Navigate routes and verify:
 *      - page_view events appear with correct page_location and page_path
 *      - Event parameters include page_title, page_location, page_path
 *      - Events only appear after consent is granted
 *
 * 6. Common Issues:
 *    - No events: Check VITE_GA_MEASUREMENT_ID is set correctly
 *    - Events not in GA4: Verify consent is granted (check localStorage)
 *    - Duplicate events: Ensure component is only rendered once in App tree
 *    - Events not tracking: Verify GoogleAnalytics is inside <BrowserRouter>
 *
 * IMPLEMENTATION NOTES:
 * - Uses Google's official recommended approach for SPAs (manual page_view tracking)
 * - Follows Consent Mode v2 for GDPR compliance (default: denied)
 * - Component must be inside <BrowserRouter> to use useLocation hook
 * - Page views only sent after user accepts cookies via CookieConsent component
 * - Consent expires after 1 year (365 days) per GDPR best practices
 */
export default function GoogleAnalytics() {
  const location = useLocation();

  // Initialize GA once on mount
  useEffect(() => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const debugMode = import.meta.env.VITE_GA_DEBUG_MODE === 'true';

    // Only load GA if measurement ID is configured
    if (!measurementId || typeof window === 'undefined') {
      if (debugMode) {
        console.log('[GA] Measurement ID not configured, skipping GA initialization');
      }
      return;
    }

    // Initialize dataLayer for gtag
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer!.push(arguments);
    };

    // Set initial consent to denied (required for GDPR compliance)
    window.gtag('consent', 'default', {
      'analytics_storage': 'denied',
      'ad_storage': 'denied',
      'wait_for_update': 500
    });

    // Initialize GA with current date
    window.gtag('js', new Date());

    // Configure GA4 with measurement ID
    const config: Record<string, any> = {
      'send_page_view': false // We'll send page_view manually on route changes
    };

    if (debugMode) {
      config.debug_mode = true;
      console.log('[GA] Debug mode enabled - events will appear in GA4 DebugView');
    }

    window.gtag('config', measurementId, config);

    // Load GA script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
    document.head.appendChild(script);

    if (debugMode) {
      console.log('[GA] Initialized with measurement ID:', measurementId);
    }

    // Cleanup
    return () => {
      script.remove();
    };
  }, []);

  // Track page views on route changes
  useEffect(() => {
    const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
    const debugMode = import.meta.env.VITE_GA_DEBUG_MODE === 'true';

    if (!measurementId || typeof window === 'undefined' || !window.gtag) {
      return;
    }

    // Check if user has accepted cookies
    const consentData = localStorage.getItem('cookie-consent');
    let hasConsent = false;

    if (consentData) {
      try {
        const { accepted, timestamp } = JSON.parse(consentData);
        const oneYearInMs = 365 * 24 * 60 * 60 * 1000;
        const consentAge = Date.now() - timestamp;
        hasConsent = accepted && timestamp && consentAge <= oneYearInMs;
      } catch (e) {
        // Invalid consent data
      }
    }

    // Only send page_view if user has consented
    if (hasConsent) {
      window.gtag('event', 'page_view', {
        page_location: window.location.href,
        page_path: location.pathname + location.search,
        page_title: document.title
      });

      if (debugMode) {
        console.log('[GA] Page view tracked:', location.pathname);
      }
    } else if (debugMode) {
      console.log('[GA] Page view not tracked - user has not consented');
    }
  }, [location]);

  return null;
}
